import React, { useState } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import './ContentBlock.scss';

type MediaPosition = 'left' | 'right';
type Style = 'default' | 'stand-out';
type Background = 'white' | 'dark' | 'orange';

type MediaDimensions = { width: number; height: number };

type MediaAsset = {
  fields: {
    title?: string;
    description?: string;
    file: {
      url: string;
      contentType: string;
      details?: {
        size?: number;
        image?: MediaDimensions;
        video?: MediaDimensions;
      };
    };
  };
};

type ContentBlockFields = {
  mediaList?: MediaAsset[];
  content: Document;
  mediaPosition?: MediaPosition;
  style?: Style;
  background?: Background;
  hideMediaOnMobile?: boolean;
};

const ContentBlock = ({ fields }: { fields: ContentBlockFields }) => {
  const {
    mediaList,
    content,
    mediaPosition = 'left',
    style = 'default',
    background,
    hideMediaOnMobile,
  } = fields;

  const firstFile = mediaList?.[0]?.fields.file;
  const firstDims = firstFile?.details?.image ?? firstFile?.details?.video;
  const metaIsPortrait = firstDims ? firstDims.height > firstDims.width : false;

  const [videoPortrait, setVideoPortrait] = useState<boolean | null>(null);
  const isPortrait = videoPortrait ?? metaIsPortrait;

  const classes = [
    'contentBlock',
    `contentBlock--${mediaPosition}`,
    style === 'stand-out' && 'contentBlock--standOut',
    background && `contentBlock--bg-${background}`,
    isPortrait && 'contentBlock--portraitMedia',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <div className={'contentBlockContent'}>
        {mediaList && mediaList.length > 0 && (
          <div className={['contentBlockMedia', hideMediaOnMobile && 'contentBlockMedia--hiddenOnMobile'].filter(Boolean).join(' ')}>
            {mediaList.map((asset, i) => {
              const { file, title, description } = asset.fields;
              if (file.contentType?.startsWith('video/')) {
                return (
                  <video
                    key={i}
                    src={`https:${file.url}`}
                    autoPlay
                    controls
                    className={'contentBlockVideo'}
                    onLoadedMetadata={
                      i === 0
                        ? (e) => {
                            const v = e.currentTarget;
                            setVideoPortrait(v.videoHeight > v.videoWidth);
                          }
                        : undefined
                    }
                  />
                );
              }
              return (
                <img
                  key={i}
                  src={`https:${file.url}`}
                  alt={description || title || ''}
                  className={'contentBlockImage'}
                />
              );
            })}
          </div>
        )}
        <div className={'contentBlockText'}>
          {documentToReactComponents(content)}
        </div>
      </div>
    </div>
  );
};

export default ContentBlock;
